import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoSearchOutline, IoCalendarOutline, IoFilterOutline, IoListOutline, IoJournalOutline, IoDownloadOutline } from 'react-icons/io5';
import moment from 'moment';
import 'moment/locale/es';
import './BitacoraAccesosScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

moment.locale('es');

function BitacoraAccesosScreen() {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [accesosGlobales, setAccesosGlobales] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('todos'); // visitas, proveedores, servicios
    const [dateFrom, setDateFrom] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));
    const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!userData || !userData.condominioId) return;

        const fetchAccesos = async () => {
            setLoading(true);
            try {
                const fromDate = moment(dateFrom, 'YYYY-MM-DD').startOf('day').toDate();
                const toDate = moment(dateTo, 'YYYY-MM-DD').endOf('day').toDate();

                const qAccesos = query(
                    collection(db, "accesos"),
                    where("condominioId", "==", userData.condominioId),
                    where("fecha", ">=", fromDate),
                    where("fecha", "<=", toDate)
                );

                const qProveedores = query(
                    collection(db, "accesos_proveedores_generales"),
                    where("condominioId", "==", userData.condominioId),
                    where("timestamp", ">=", fromDate),
                    where("timestamp", "<=", toDate)
                );

                const qUsuarios = query(
                    collection(db, "usuarios"),
                    where("condominioId", "==", userData.condominioId)
                );

                const [snapAccesos, snapProveedores, snapUsuarios] = await Promise.all([
                    getDocs(qAccesos),
                    getDocs(qProveedores),
                    getDocs(qUsuarios).catch(err => {
                        console.warn("Aviso al consultar usuarios en bitácora:", err);
                        return { forEach: () => {} };
                    })
                ]);

                const residentMap = {};
                if (snapUsuarios && snapUsuarios.forEach) {
                    snapUsuarios.forEach(uDoc => {
                        residentMap[uDoc.id] = uDoc.data();
                    });
                }

                let combined = [];

                snapAccesos.forEach(doc => {
                    const data = doc.data();

                    // Excluir pases maestros multi-uso o ilimitados (estos son contenedores de pase, los registros individuales se generan en logs)
                    const isMasterMultiUsePass = (data.esIlimitado === true || data.usosMaximos > 1 || data.usosMaximos === -1) && !data.accesoOriginalId && !data.esEventoBitacora;
                    if (isMasterMultiUsePass && data.estatus === 'Activo') {
                        return;
                    }

                    if (data.estatus === 'Completado' || data.estatus === 'Usado' || data.estatus === 'Denegado' || data.usosActuales > 0 || data.esEventoBitacora) {
                        let rawCasa = (data.numeroCasa || '').toString().trim();
                        let cleanCasa = rawCasa.replace(/^(casa|depto)\s*/i, '').trim();
                        let hasValidCasa = cleanCasa && cleanCasa !== 'S/N' && cleanCasa !== 'N/A' && cleanCasa !== 'Sin Unidad' && cleanCasa !== 'null' && cleanCasa !== 'undefined';

                        // Si el documento de acceso no guardó la casa o dice S/N, resolverla en vivo desde el usuario residente
                        if (!hasValidCasa && data.residenteId && residentMap[data.residenteId]) {
                            const res = residentMap[data.residenteId];
                            const candCasa = res.numeroCasa || res.casaNumero || res.residencia || res.depto || res.unidadResidente || res.unidad || '';
                            if (candCasa && candCasa !== 'S/N' && candCasa !== 'N/A' && candCasa !== 'Sin Unidad') {
                                cleanCasa = String(candCasa).replace(/^(casa|depto)\s*/i, '').trim();
                                hasValidCasa = true;
                            }
                        }

                        let destinoText = 'General / Coto';
                        if (hasValidCasa) {
                            destinoText = `Casa ${cleanCasa}`;
                        } else if (data.residenteNombre) {
                            destinoText = `Residente (${data.residenteNombre})`;
                        } else if (data.residenteId) {
                            const res = residentMap[data.residenteId];
                            destinoText = res?.nombre ? `Residente (${res.nombre})` : 'Residente';
                        }

                        const tipoIngreso = data.tipoDetallado || data.tipo || 'Visita';

                        combined.push({
                            ...data,
                            id: doc.id,
                            fecha: data.fecha || data.timestamp,
                            visitante: data.nombreVisita || data.nombre || data.Proveedor || 'Desconocido',
                            destino: destinoText,
                            tipo: tipoIngreso,
                            origen: 'accesos',
                            origenBd: data.origen || null,
                        });
                    }
                });

                snapProveedores.forEach(doc => {
                    const data = doc.data();
                    combined.push({
                        id: doc.id,
                        fecha: data.timestamp,
                        visitante: data.proveedorNombre || data.nombre || data.proveedorId || 'Servicio General',
                        destino: 'General / Coto',
                        tipo: 'Servicio Público',
                        origen: 'proveedores_generales',
                        ...data
                    });
                });

                combined = combined.filter(item => item.fecha).sort((a, b) => {
                    const dateA = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha);
                    const dateB = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha);
                    return dateB - dateA;
                });

                setAccesosGlobales(combined);

            } catch (error) {
                console.error("Error fetching bitacora:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAccesos();
    }, [userData, dateFrom, dateTo]);

    const handleGenerateReport = () => {
        if (filteredAccesos.length === 0) {
            alert("No hay registros para exportar con los filtros actuales.");
            return;
        }

        const headers = ["Fecha", "Hora", "Visitante / Entidad", "Destino", "Tipo de Ingreso", "Acceso / Puerta"];
        const rows = filteredAccesos.map(acceso => {
            const accesoDate = acceso.fecha?.toDate ? acceso.fecha.toDate() : new Date(acceso.fecha);
            return [
                moment(accesoDate).format('YYYY-MM-DD'),
                moment(accesoDate).format('HH:mm'),
                `"${(acceso.visitante || '').replace(/"/g, '""')}"`,
                `"${(acceso.destino || '').replace(/"/g, '""')}"`,
                `"${(acceso.tipo || '').replace(/"/g, '""')}"`,
                `"${(acceso.puerta || acceso.vigilanteName || 'Caseta / Pluma Principal').replace(/"/g, '""')}"`
            ].join(",");
        });

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Bitacora_Accesos_${moment().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const filteredAccesos = accesosGlobales.filter(acceso => {
        const matchesSearch =
            (acceso.visitante || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (acceso.destino || '').toLowerCase().includes(searchTerm.toLowerCase());

        let matchesType = true;
        if (filterType === 'visitas') matchesType = (acceso.tipo || '').toLowerCase().includes('visita');
        if (filterType === 'proveedores_generales') matchesType = acceso.origen === 'proveedores_generales';
        if (filterType === 'proveedor_acceso') matchesType = acceso.origen === 'accesos' && (acceso.tipo || '').toLowerCase().includes('proveedor') && acceso.origenBd !== 'Marketplace';
        if (filterType === 'proveedor_marketplace') matchesType = acceso.origen === 'accesos' && (acceso.tipo || '').toLowerCase().includes('proveedor') && acceso.origenBd === 'Marketplace';
        if (filterType === 'servicios') matchesType = (acceso.tipo || '').toLowerCase().includes('envio') || (acceso.tipo || '').toLowerCase().includes('servicio') || (acceso.tipo || '').toLowerCase().includes('entrega') || (acceso.tipo || '').toLowerCase().includes('paqueter');

        return matchesSearch && matchesType;
    });

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/bitacora" />

            <main className="ks-main">
                <KondoBrandHeader title="Administración" showBackButton={false} />

                <div className="ks-scroll-area">
                    <div className="bitacora-container">
                        {/* --- Premium Banner --- */}
                        <div className="muro-header-stitch">
                            <div className="muro-hero-card">
                                <div className="muro-hero-content">
                                    <h1>Bitácora</h1>
                                    <p>Registro global de ingresos al fraccionamiento</p>

                                    <button className="muro-hero-action-btn" onClick={handleGenerateReport}>
                                        <IoDownloadOutline size={20} />
                                        <span>Exportar</span>
                                    </button>
                                </div>
                                <IoJournalOutline className="muro-hero-icon-decor" />
                            </div>
                        </div>

                        <div className="bitacora-main">
                            <div className="filters-card">
                                <div className="filter-group-header">Filtros de Búsqueda</div>
                                <div className="filters-grid">
                                    <div className="filter-input-wrapper">
                                        <IoSearchOutline className="filter-icon" />
                                        <input
                                            type="text"
                                            placeholder="Buscar nombre o casa..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>

                                    <div className="filter-input-wrapper">
                                        <IoCalendarOutline className="filter-icon" />
                                        <input
                                            type="date"
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                        />
                                    </div>

                                    <div className="filter-input-wrapper">
                                        <IoCalendarOutline className="filter-icon" />
                                        <input
                                            type="date"
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                        />
                                    </div>

                                    <div className="filter-input-wrapper">
                                        <IoFilterOutline className="filter-icon" />
                                        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                                            <option value="todos">Todos los Tipos</option>
                                            <option value="visitas">Solo Visitas/Invitados</option>
                                            <option value="proveedores_generales">Servicios Públicos (Basura, Gas)</option>
                                            <option value="proveedor_acceso">Proveedores (Accesos Comunes)</option>
                                            <option value="proveedor_marketplace">Proveedores (Marketplace)</option>
                                            <option value="servicios">Plataformas Ext. (Uber/Amazon/Didi)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="bitacora-table-container">
                                {loading ? (
                                    <div className="loading-state">Cargando registros...</div>
                                ) : (
                                    filteredAccesos.length === 0 ? (
                                        <div className="empty-row">No se encontraron accesos en este rango.</div>
                                    ) : (
                                        isMobile ? (
                                            <div className="mobile-bitacora-list">
                                                {filteredAccesos.map((acceso, idx) => {
                                                    const accesoDate = acceso.fecha?.toDate ? acceso.fecha.toDate() : new Date(acceso.fecha);
                                                    const isDenied = acceso.estatus === 'Denegado';
                                                    return (
                                                        <div key={acceso.id || idx} className={`mobile-bitacora-card ${isDenied ? 'denied-card' : ''}`}>
                                                            <div className="mbc-header">
                                                                <span className="mbc-visitor">{acceso.visitante}</span>
                                                                <div className="mbc-date-time">
                                                                    <span className="mbc-date">{moment(accesoDate).format('DD MMM, YYYY')}</span>
                                                                    <span className="mbc-time">{moment(accesoDate).format('hh:mm A')}</span>
                                                                </div>
                                                            </div>
                                                            <div className="mbc-body">
                                                                <div className="mbc-info-row">
                                                                    <span className="mbc-info-label">Destino</span>
                                                                    <span className="mbc-info-value">{acceso.destino}</span>
                                                                </div>
                                                                <div className="mbc-info-row">
                                                                    <span className="mbc-info-label">Tipo</span>
                                                                    <span className={`type-badge ${acceso.origen}`}>{acceso.tipo}</span>
                                                                </div>
                                                                <div className="mbc-info-row">
                                                                    <span className="mbc-info-label">Estatus</span>
                                                                    <span className={`status-pill ${isDenied ? 'denegado' : 'otorgado'}`}>{isDenied ? 'Denegado' : 'Otorgado'}</span>
                                                                </div>
                                                                <div className="mbc-info-row">
                                                                    <span className="mbc-info-label">Acceso/Puerta</span>
                                                                    <span className="mbc-info-value">{acceso.puerta || acceso.vigilanteName || 'Caseta / Pluma Principal'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <table className="modern-table">
                                                <thead>
                                                    <tr>
                                                        <th>Fecha y Hora</th>
                                                        <th>Visitante / Entidad</th>
                                                        <th>Destino</th>
                                                        <th>Tipo de Ingreso</th>
                                                        <th>Acceso / Puerta</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredAccesos.map((acceso, idx) => {
                                                        const accesoDate = acceso.fecha?.toDate ? acceso.fecha.toDate() : new Date(acceso.fecha);
                                                        const isDenied = acceso.estatus === 'Denegado';
                                                        return (
                                                            <tr key={acceso.id || idx} className={isDenied ? 'denied-row' : ''}>
                                                                <td className="col-time">
                                                                    <div className="date-block">
                                                                        <span className="date">{moment(accesoDate).format('DD MMM, YYYY')}</span>
                                                                        <span className="time">{moment(accesoDate).format('HH:mm A')}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="col-visitor">
                                                                    <strong>{acceso.visitante}</strong>
                                                                </td>
                                                                <td>{acceso.destino}</td>
                                                                <td>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <span className={`type-badge ${acceso.origen}`}>
                                                                            {acceso.tipo}
                                                                        </span>
                                                                        <span className={`status-pill ${isDenied ? 'denegado' : 'otorgado'}`}>
                                                                            {isDenied ? 'Denegado' : 'Otorgado'}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="col-guard">
                                                                    {acceso.puerta || acceso.vigilanteName || 'Caseta / Pluma Principal'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        )
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <KondoBottomNav activeRoute="/bitacora" />
        </div>
    );
}

export default BitacoraAccesosScreen;
